import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Box } from '@comeon/ui';
import { notificationCarouselShown, notificationCarouselClosed, notificationPopupXClicked } from '@comeon/analytics';

import breakpoint, { getZindexByType } from '~/utils/style-utils';
import Slider from '~/components/slider/index.js';
import { selectSwedishRegulatedFranchise } from '~/components/brand/ducks/selectors';
import { showWelcomePopupSelector } from '~/components/authentication/ducks/selectors';
import { getCookies, setPermanentCookie } from '~/store/cookies.js';
import { isBridgeAvailable } from '~/components/app-bridge/index.js';

import MyNotificationSliderItem from './my-notification-slider-item.js';
import { removeNotificationPopupFromQueue, markReceived } from '../ducks/my-notifications.js';
import useDebounceEffect from '../utils/useDebounceEffect.js';

import useLayoutSize from '../../../../hooks/use-layout-size.js';
import { SliderArrowStyle } from '../../../slider/SliderComponents.js';
import { findAvailableMaxHeight } from '../utils/truncateLengthCalculator.js';

import useUIContext from '../../../../ui/use-ui-context';
import usePreprocess from '../utils/usePreprocess.js';

const NotificationSliderWrapper = styled(Box).attrs({
	pt: { _: 8, lg: 15 },
	pb: 8
})`
	z-index: ${getZindexByType('inbox-notification-slider')};
	width: 100%;
	height: 70%;
	min-height: 575px;
	margin-left: ${props => (props.changeCarouselPosition ? `calc(50% - 487px)` : 0)};

	${breakpoint.lg`
		max-width: 600px;
		padding-left: 24px;
		padding-right: 24px;
	`}

	filter: ${({ displayBlurred }) => (displayBlurred ? 'blur(7px)' : '')};
`;

const NotificationSliderBackdrop = styled.div`
	top: ${props => (props.hasRegulationsHeader ? `var(--regulationHeight)` : 0)};
	position: fixed;
	bottom: 0;
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: ${props => (props.changeCarouselPosition ? `flex-start` : `center`)};
	background-color: rgba(var(--notification-popup-blur-bg), 0.8);
	z-index: ${getZindexByType('inbox-notification-slider')};
`;

const isPopupOnCooldown = () => {
	return getCookies('popupCooldown');
};

const checkIfPopupCanBeDisplayed = (
	notification,
	isSmall,
	currentPathName,
	shouldShowWelcomePageDetails,
	myNotificationsPopupWhitelistURL
) => {
	// dont show popup notification when welcome screen or the welcome popup is displayed
	if (shouldShowWelcomePageDetails === true) {
		return false;
	}
	if (isPopupOnCooldown()) {
		return false;
	}
	let pathname = currentPathName;
	// this is done to remove the trailing / in the pathname for all routes other than the root route
	if (pathname && pathname.length > 1 && pathname.substr(-1) === '/') {
		pathname = pathname.slice(0, -1);
	}

	// Check for custom routes first
	const customRoutes = notification?.data?.CustomDisplayRulesOptions
		? notification.data.CustomDisplayRulesOptions
		: [];

	if (customRoutes) {
		let isRouteOkay = false;
		if (customRoutes.includes(pathname)) {
			isRouteOkay = true;
		} else if (
			(pathname.includes(`/casino/game`) && customRoutes.includes(`/casino/game`)) ||
			(pathname.includes(`/slots/game`) && customRoutes.includes(`/slots/game`))
		) {
			isRouteOkay = true;
		}

		if (isRouteOkay) {
			if (isSmall) {
				const urlParams = new URLSearchParams(window.location.search);
				if (urlParams.get('sidebar')) {
					return false;
				}
			}
			return true;
		}
	}

	// If custom routes are not available, check if current route is allowed or not
	if (
		(!customRoutes || customRoutes.length === 0) &&
		myNotificationsPopupWhitelistURL &&
		myNotificationsPopupWhitelistURL.includes(pathname)
	) {
		if (isSmall) {
			const urlParams = new URLSearchParams(window.location.search);
			if (urlParams.get('sidebar')) {
				return false;
			}
		}
		return true;
	}
	// default return false
	return false;
};

const setPopupCooldownCookie = expiryInMinutes => {
	const expiryDate = new Date(Date.now() + expiryInMinutes * 60 * 1000);
	// document.cookie = `popupCooldown=true; expires=${expiryDate.toUTCString()};path='/'`;
	setPermanentCookie('popupCooldown', true, expiryDate);
};

// Will iterate over the given popup queue
// and return the first available popup
// for the current route.
// Will return undefined if there is none.
const getAvailablePopupsAtRoute = (
	popupArray,
	myNotificationsPopupWhitelistURL,
	isSmall,
	currentPathName,
	shouldShowWelcomePageDetails
) => {
	// Sort the array by startDate so that we never change the popup positions, even if we have an update
	const sortedPopupArray = popupArray.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
	const notificationToPopup = sortedPopupArray.filter(notification => {
		return checkIfPopupCanBeDisplayed(
			notification,
			isSmall,
			currentPathName,
			shouldShowWelcomePageDetails,
			myNotificationsPopupWhitelistURL
		);
	});

	return notificationToPopup;
};

const MyNotificationSliderItemWrapper = ({
	notification,
	popupsToDisplay,
	currentSlide,
	handleCloseButtonClick,
	i,
	seenItemIds,
	small,
	bridgeAvailable,
	sliderWrapperRef,
	markSeenItemsAsReceived
}) => {
	const item = usePreprocess(notification);
	return (
		<MyNotificationSliderItem
			key={`${item.id}`}
			id={item.id}
			recurringNotificationId={item.recurringNotificationId}
			notificationPopUpData={{ ...item.notificationPopUp, personalizationData: item.personalizationData }}
			navigationDotsCount={popupsToDisplay.length}
			activeNavigationDotsIndex={currentSlide}
			handleCloseButtonClick={handleCloseButtonClick}
			showCloseButton={currentSlide === i}
			numberSeenItems={seenItemIds.size}
			maxAvailableHeight={findAvailableMaxHeight(
				!!(small || bridgeAvailable),
				sliderWrapperRef?.current ? sliderWrapperRef.current.clientHeight : null,
				item.notificationPopUp?.data
			)}
			isMobileDevice={small || bridgeAvailable}
			markSeenItemsAsReceived={markSeenItemsAsReceived}
		/>
	);
};

/**
 * Notification popup slider.
 * Will resize itself and maintain a decent behavior on different screensizes.
 *
 * The rendering component should keep the open state itself and pass in a
 * callback for closing the modal to this component.
 *
 * See proptypes for shape of object in items array.
 */
const MyNotificationSlider = () => {
	const ref = useRef(null);
	const popupQueue = useSelector(state => state.myNotifications.popupQueue);
	const myNotificationsPopupWhitelistURL = useSelector(state => state.brand.myNotificationsPopupWhitelistURL);
	const shouldShowWelcomePageDetails = useSelector(showWelcomePopupSelector);
	const popupCooldownTimeInMinutes = useSelector(state => state.brand.popupCooldownTimeInMinutes);
	const hasRegulationsHeader = useSelector(selectSwedishRegulatedFranchise);
	const bridgeAvailable = useMemo(() => isBridgeAvailable(), []);

	const location = useLocation();
	const dispatch = useDispatch();
	const { small } = useLayoutSize();
	const { showSidebar, hasNotificationPopupsBlurred } = useUIContext();

	const [currentSlide, setCurrentSlide] = useState(0);
	const [seenItemIds, setSeenItemsIds] = useState(new Set());

	const popupsToDisplay = useMemo(
		() =>
			getAvailablePopupsAtRoute(
				Object.values(popupQueue),
				myNotificationsPopupWhitelistURL,
				!!(small || bridgeAvailable),
				location.pathname,
				shouldShowWelcomePageDetails
			),
		[
			small,
			bridgeAvailable,
			location.pathname,
			myNotificationsPopupWhitelistURL,
			popupQueue,
			shouldShowWelcomePageDetails
		]
	);

	const gotoNextSlide = () => {
		if (currentSlide < popupsToDisplay.length - 1) {
			const newSlide = currentSlide + 1;
			setCurrentSlide(newSlide);
			if (!seenItemIds.has(popupsToDisplay[newSlide].id)) {
				setSeenItemsIds(new Set([...seenItemIds, popupsToDisplay[newSlide].id]));
			}
		}
	};

	const gotoPreviousSlide = () => {
		if (currentSlide > 0) {
			const newSlide = currentSlide - 1;
			setCurrentSlide(newSlide);
			if (!seenItemIds.has(popupsToDisplay[newSlide].id)) {
				setSeenItemsIds(new Set([...seenItemIds, popupsToDisplay[newSlide].id]));
			}
		}
	};

	const markSeenItemsAsReceived = useCallback(() => {
		[...seenItemIds].forEach(itemId => {
			dispatch(markReceived(itemId));
			dispatch(removeNotificationPopupFromQueue(itemId));
		});
		setPopupCooldownCookie(popupCooldownTimeInMinutes);
		setCurrentSlide(0);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [popupCooldownTimeInMinutes, seenItemIds]);

	const handleBackdropClick = event => {
		if (event.target === event.currentTarget) {
			markSeenItemsAsReceived();
			notificationCarouselClosed(`${seenItemIds.size}/${popupsToDisplay.length}`);
		}
	};

	const handleCloseButtonClick = label => {
		markSeenItemsAsReceived();
		notificationPopupXClicked(label);
		notificationCarouselClosed(`${seenItemIds.size}/${popupsToDisplay.length}`);
	};

	useEffect(() => {
		if (!!popupsToDisplay && popupsToDisplay.length > 0 && !seenItemIds.has(popupsToDisplay[0].id)) {
			setSeenItemsIds(new Set([...seenItemIds, popupsToDisplay[0].id]));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [popupsToDisplay]);

	useDebounceEffect(
		() => {
			if (popupsToDisplay?.length > 0) {
				notificationCarouselShown(popupsToDisplay.length);
			}
		},
		1000,
		[popupsToDisplay]
	);

	if (popupsToDisplay.length === 0) {
		return null;
	}

	return (
		<NotificationSliderBackdrop
			hasRegulationsHeader={hasRegulationsHeader}
			onClick={handleBackdropClick}
			data-at="my-notification-slider-backdrop"
			changeCarouselPosition={!(small || bridgeAvailable) && showSidebar}
		>
			<NotificationSliderWrapper
				ref={ref}
				changeCarouselPosition={!(small || bridgeAvailable) && showSidebar}
				displayBlurred={hasNotificationPopupsBlurred}
			>
				<Slider
					onNext={gotoNextSlide}
					onPrevious={gotoPreviousSlide}
					currentSlide={currentSlide}
					displayArrows={!(small || bridgeAvailable)}
					arrowStyle={SliderArrowStyle.covering}
					arrowsTopPosition={['50%', '50%']}
				>
					{popupsToDisplay.map((item, i) => (
						<MyNotificationSliderItemWrapper
							key={`${item.id}`}
							notification={item}
							popupsToDisplay={popupsToDisplay}
							currentSlide={currentSlide}
							handleCloseButtonClick={handleCloseButtonClick}
							i={i}
							seenItemIds={seenItemIds}
							small={small}
							bridgeAvailable={bridgeAvailable}
							sliderWrapperRef={ref}
							markSeenItemsAsReceived={markSeenItemsAsReceived}
						/>
					))}
				</Slider>
			</NotificationSliderWrapper>
		</NotificationSliderBackdrop>
	);
};

export default MyNotificationSlider;
